/**
 * A value that clears itself after a delay.
 *
 * The "just added X" / "undo" / "combo" toast pattern. Extracted while
 * decomposing `ExtrasManager`, where it was a `useState` + a `useRef` timer + a
 * setter that clears the previous timer + an unmount-cleanup effect — four
 * pieces that only work as a set.
 *
 * Several places in the app hand-roll this shape. Auditing them found that most
 * DID clean up correctly — `ComboBadge` and `RecentRatingFeedback` both did —
 * so the "they all leak" assumption was wrong. `PaymentSection` was the real
 * offender: it armed a 12s poll from an event handler with no ref and no
 * cleanup, so leaving the billing page right after upgrading called `setState`
 * on an unmounted component. It now uses this hook.
 *
 * Use this for the **imperative** case (a handler produces a message). For the
 * declarative "a prop changed, flash something" case see `useTransientFlag`,
 * which is what the badges actually needed.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useTransientValue<T>(durationMs: number) {
  const [value, setValue] = useState<T | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** Show `next`, replacing any value already counting down. */
  const show = useCallback(
    (next: T) => {
      setValue(next);
      // Reset rather than stack: a second call must restart the window, not
      // let the first timer cut the second value short.
      clearTimer();
      timerRef.current = window.setTimeout(() => {
        setValue(null);
        timerRef.current = null;
      }, durationMs);
    },
    [durationMs, clearTimer]
  );

  /** Clear immediately — e.g. the surface hosting it is closing. */
  const reset = useCallback(() => {
    clearTimer();
    setValue(null);
  }, [clearTimer]);

  // The piece the hand-rolled copies keep missing: without this, unmounting
  // mid-window leaves a timer that calls setState on a dead component.
  useEffect(() => clearTimer, [clearTimer]);

  return { value, show, reset };
}
